import {
  TableCellType,
  TableHeader,
} from "../../../components/custom-table/table";
import { PharmaStpFileModel } from "../../../data-model/nycbsPharmaOrder";
import {
  TableCellMultiline,
  TableCellWithBadge,
} from "../../../data-model/tableCells";
import { NycbsExternalTableDataModel } from "../../../data-model/tablesData";
import { NycbsExternalTableColumnKeys } from "../../../enums/tableColumnKeys";
import { formatDob } from "./utils/formatDob";
import { extractFormattedDateFromFileName } from "./utils/extractDateFromFileName";
import { getBlackViewBadge } from "./utils/getBlackViewBadge";
import { getNycbsOutcomeClassBadge } from "./utils/getNycbsOutcomeClassBadge";
import { getOrderStatusBadgeStyles } from "./utils/getOrderStatusBadgeStyles";
import { getPharmacyTypeBadgeStyles } from "./utils/getPharmacyTypeBadgeStyles";
import { isNycbsSummaryViewDisabled } from "./utils/isNycbsSummaryViewDisabled";
import {
  getNycbsClientOutcomeBadgeStyles,
  getNycbsClientOutcomeDisplayText,
} from "./utils/nycbsClientOutcome";
import { mapNycbsExternalExtendedRowFields } from "./mapNycbsExternalExtendedRowFields";

export const NycbsExternalTableHeader: TableHeader[] = [
  {
    label: "DOW",
    key: NycbsExternalTableColumnKeys.DATE_OF_WORK,
    order: 1,
    width: 8,
    sortable: true,
    type: TableCellType.STRING,
  },
  {
    label: "Patient Name",
    key: NycbsExternalTableColumnKeys.PATIENT_NAME,
    order: 2,
    width: 12,
    sortable: false,
    type: TableCellType.MULTILINE,
  },
  {
    label: "DOB",
    key: NycbsExternalTableColumnKeys.DOB,
    order: 3,
    width: 8,
    sortable: false,
    type: TableCellType.STRING,
  },
  {
    label: "Pharmacy Type",
    key: NycbsExternalTableColumnKeys.PHARMACY_TYPE,
    order: 4,
    width: 12,
    sortable: false,
    type: TableCellType.BADGE,
  },
  {
    label: "Drug",
    key: NycbsExternalTableColumnKeys.DRUG,
    order: 5,
    width: 14,
    sortable: false,
    type: TableCellType.STRING,
  },
  {
    label: "CoverMyMeds ID",
    key: NycbsExternalTableColumnKeys.CMM_ID,
    order: 6,
    width: 10,
    sortable: false,
    type: TableCellType.STRING,
  },
  {
    label: "Outcome",
    key: NycbsExternalTableColumnKeys.OUTCOME,
    order: 7,
    width: 12,
    sortable: false,
    type: TableCellType.BADGE,
  },
  {
    label: "Outcome Class",
    key: NycbsExternalTableColumnKeys.OUTCOME_CLASS,
    order: 8,
    width: 12,
    sortable: false,
    type: TableCellType.BADGE,
  },
  {
    label: "Summary",
    key: NycbsExternalTableColumnKeys.SUMMARY,
    order: 9,
    width: 7,
    sortable: false,
    type: TableCellType.BADGE_BUTTON,
  },
  {
    label: "Log",
    key: NycbsExternalTableColumnKeys.LOG,
    order: 10,
    width: 7,
    sortable: false,
    type: TableCellType.BADGE_BUTTON,
  },
  {
    label: "Order Status",
    key: NycbsExternalTableColumnKeys.ORDER_STATUS_BADGE,
    order: 11,
    width: 10,
    sortable: false,
    type: TableCellType.BADGE,
  },
];

/**
 * Maps NYCBS External API rows into PA Requests table row data.
 *
 * @input data - Raw orders from the NYCBS External API
 * @return NycbsExternalTableDataModel[]: Formatted rows for CustomTable
 */
export const generateTableDataForNycbsExternalTable = (
  data: PharmaStpFileModel[],
): NycbsExternalTableDataModel[] => {
  return data.map((item) => {
    const fileName: string = item?.filename ?? "";
    const dow: string = extractFormattedDateFromFileName(fileName);
    const identifier: string = item?.identifier ?? "";
    const patient_mrn: string = item?.patient_mrn ?? "";

    const patientName: TableCellMultiline = {
      mainText: item?.patient_name ?? "",
      secondaryText: patient_mrn ? `MRN : ${patient_mrn}` : "",
    };

    const dob: string = formatDob(item?.dob);
    const pharmacyType: TableCellWithBadge = getPharmacyTypeBadgeStyles(
      item?.pharmacy_type,
    );
    const drug: string = item?.drug ?? "";

    const cmmIdValue = item?.covermymed_id;
    const cmmId: string =
      !cmmIdValue || cmmIdValue === "nan" || cmmIdValue === "NaN"
        ? ""
        : String(cmmIdValue);

    const rawResponseStatus = item?.response_status ?? null;
    const outcomeDisplayText = getNycbsClientOutcomeDisplayText(rawResponseStatus);
    const outcomeStyles = getNycbsClientOutcomeBadgeStyles(outcomeDisplayText);
    const outcomeBadge: TableCellWithBadge = {
      text: outcomeDisplayText,
      color: outcomeStyles.color,
      bgColor: outcomeStyles.bgColor,
      hoverText: outcomeDisplayText,
    };

    const outcomeClass: TableCellWithBadge = getNycbsOutcomeClassBadge({
      clientOutcomeLabel: outcomeDisplayText,
      responseStatus: rawResponseStatus,
      paOutcomeClassification: item?.pa_outcome_classification,
    });

    const cmmStatus = item.meta?.cmm_st_rpa_status;
    const summary: TableCellWithBadge = getBlackViewBadge(
      isNycbsSummaryViewDisabled(rawResponseStatus, cmmStatus),
    );
    const log: TableCellWithBadge = getBlackViewBadge(false);
    const orderStatusBadge: TableCellWithBadge = getOrderStatusBadgeStyles(
      item?.sftp_status,
    );

    const extendedFields = mapNycbsExternalExtendedRowFields(item);

    return {
      dow,
      patientName,
      dob,
      pharmacyType,
      drug,
      cmmId,
      responseStpStatus: outcomeBadge,
      cmm: summary,
      textNote: log,
      orderStatusBadge,
      identifier,
      textNoteStatus: item.text_note_status,
      cmmStatus,
      responseStatusValue: item?.response_status ?? "",
      patient_mrn,
      patient_name: item.patient_name ?? "",
      pharmacy_type: item.pharmacy_type ?? "",
      response_status: item.response_status ?? null,
      ...extendedFields,
      paStatusBucket: outcomeClass,
    };
  });
};
